import React, { useRef, useState, useCallback } from "react";
import { Upload, X, FileText, Image, File, FileSpreadsheet, Presentation } from "lucide-react";
import cn from "./cn";
import IconButton from "./IconButton";
import DropZone from "./DropZone";
import { Label, FieldError, HelpText } from "./FormField";

const FILE_ICONS = {
  "application/pdf": FileText,
  "text/plain": FileText,
  "application/msword": FileText,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": FileText,
  "image/jpeg": Image,
  "image/png": Image,
  "image/webp": Image,
};

// A browser's MIME for a spreadsheet or a deck is unreliable (a .csv can arrive
// as application/vnd.ms-excel or as nothing), so these go by the name.
const EXTENSION_ICONS = {
  csv: FileSpreadsheet,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  ppt: Presentation,
  pptx: Presentation,
};

const extensionOf = (name = "") => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
};

const iconFor = (file) => EXTENSION_ICONS[extensionOf(file.name)] || FILE_ICONS[file.type] || File;

/**
 * Whether a file is one `accept` allows, by the browser's own rules: `.ext`
 * against the name, `type/*` against the start of the MIME, `type/sub` exactly.
 * The picker applies `accept` itself; a DROPPED file never passes through it,
 * which is why this exists.
 */
export const acceptsFile = (accept, file) => {
  const tokens = String(accept || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (!tokens.length) return true;
  const ext = `.${extensionOf(file.name)}`;
  const type = String(file.type || "").toLowerCase();
  return tokens.some((t) => {
    if (t.startsWith(".")) return t === ext;
    if (t.endsWith("/*")) return type.startsWith(t.slice(0, -1));
    return t === type;
  });
};

/** "PDF, DOCX, CSV" from an accept string's extensions — or its MIME subtypes when it lists none. */
const typesFromAccept = (accept) => {
  const tokens = String(accept || "").split(",").map((t) => t.trim()).filter(Boolean);
  const exts = tokens.filter((t) => t.startsWith(".")).map((t) => t.slice(1).toUpperCase());
  const names = exts.length ? exts : tokens.map((t) => (t.split("/")[1] || t).replace("*", "any").toUpperCase());
  return [...new Set(names)].join(", ");
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/** One row of the selected-file list. */
const FileRow = ({ file, onRemove }) => {
  const Icon = iconFor(file);
  return (
    <li className="tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-line-soft tw-bg-surface-raised tw-px-3 tw-py-2 tw-text-[0.8125rem]">
      <Icon size={18} className="tw-shrink-0 tw-text-content-muted" aria-hidden="true" />
      <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-content" title={file.name}>
        {file.name}
      </span>
      <span className="tw-shrink-0 tw-text-xs tw-text-content-subtle">
        {formatFileSize(file.size)}
      </span>
      {onRemove && (
        <IconButton
          label={`Remove ${file.name}`}
          icon={X}
          size="xs"
          variant="ghost-destructive"
          onClick={onRemove}
        />
      )}
    </li>
  );
};

/**
 * FileUpload — drag-and-drop file picker with a selected-file list.
 *
 * The drop zone is a real `<button>`, so Enter, Space, the disabled semantics
 * and the focus ring come from the platform. The `<input type="file">` is a
 * SIBLING of it rather than a child — interactive content nested inside a button
 * is invalid HTML and browsers recover from it inconsistently — and the button
 * opens the input by ref.
 *
 * Props:
 *   files      — array of File objects (controlled)
 *   onChange   — (files: File[]) => void
 *   accept     — accept string (default: common doc/image types). Enforced on
 *                drop as well as in the picker.
 *   multiple   — allow multiple files (default: true)
 *   maxFiles   — max file count (default: 5)
 *   maxSizeMB  — max size per file in MB (default: 10)
 *   disabled   — disable the upload area
 *   label      — label text above the drop zone
 *   hint       — headline text inside the drop zone
 *   typesHint  — the small line under it; derived from `accept` and
 *                `maxSizeMB` when omitted
 *   validate   — (file) => reason | null, a caller's own rule on top
 */
const FileUpload = ({
  files = [],
  onChange,
  accept = ".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png,.webp",
  multiple = true,
  maxFiles = 5,
  maxSizeMB = 10,
  disabled = false,
  className = "",
  label,
  hint,
  typesHint,
  validate,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const reactId = React.useId();
  const labelId = label ? `upload-${reactId}-label` : undefined;
  const hintId = `upload-${reactId}-hint`;

  const maxBytes = maxSizeMB * 1024 * 1024;

  const validateAndAdd = useCallback(
    (incoming) => {
      const valid = [];
      const problems = [];
      for (const f of incoming) {
        if (files.length + valid.length >= maxFiles) {
          problems.push(`Maximum ${maxFiles} files allowed.`);
          break;
        }
        if (!acceptsFile(accept, f)) {
          problems.push(`"${f.name}" isn't a file type this takes.`);
          continue;
        }
        if (f.size > maxBytes) {
          problems.push(`"${f.name}" exceeds ${maxSizeMB} MB limit.`);
          continue;
        }
        const own = validate?.(f);
        if (own) {
          problems.push(own);
          continue;
        }
        // Deduplicate by name+size
        const dup = files.find((ex) => ex.name === f.name && ex.size === f.size);
        if (dup) continue;
        valid.push(f);
      }
      setError(problems.join(" "));
      if (valid.length) onChange([...files, ...valid]);
    },
    [files, onChange, maxFiles, maxBytes, maxSizeMB, accept, validate]
  );

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    if (e.dataTransfer.files?.length) {
      validateAndAdd(Array.from(e.dataTransfer.files));
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files?.length) {
      validateAndAdd(Array.from(e.target.files));
    }
    e.target.value = "";
  };

  const removeFile = (idx) => {
    if (disabled) return;
    onChange(files.filter((_, i) => i !== idx));
    setError("");
  };

  return (
    <div className={cn("tw-w-full", className)}>
      {label && <Label id={labelId} className="tw-mb-1.5 tw-block">{label}</Label>}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="tw-hidden"
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />

      <DropZone
        state={dragActive ? "active" : "idle"}
        disabled={disabled}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        onClick={() => inputRef.current?.click()}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <Upload size={24} className="tw-text-content-subtle" aria-hidden="true" />
        <span className="tw-text-center tw-text-sm tw-font-medium tw-text-content">
          {hint || "Drag & drop files here, or click to browse"}
        </span>
        <HelpText id={hintId} className="tw-text-center">
          {typesHint || `${typesFromAccept(accept)} — Max ${maxSizeMB} MB${multiple ? " each" : ""}`}
        </HelpText>
      </DropZone>

      {error && <FieldError className="tw-mt-1.5">{error}</FieldError>}

      {files.length > 0 && (
        <ul className="tw-m-0 tw-mt-2 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
          {files.map((file, idx) => (
            <FileRow
              key={`${file.name}-${idx}`}
              file={file}
              onRemove={disabled ? undefined : () => removeFile(idx)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
